import React, { useState, useEffect } from 'react';
import { useDebouncedValue } from '../../../hooks/useDebouncedValue';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Button,
    TextField,
    Modal,
    Typography,
    IconButton,
    Chip,
    Tooltip,
    Paper,
    Stack,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    InputLabel
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    Visibility as ViewIcon,
    Timeline as TimelineIcon,
    ToggleOn as ToggleOnIcon,
    ToggleOff as ToggleOffIcon
} from '@mui/icons-material';
import { useApiHandler } from '../../../hooks/useApiHandler';
import { activityService } from '../../../services/activityService';
import ActivityForm from './ActivityForm';

const ActivityList = ({ onDataChange }) => {
    const { handleApiCall, showSuccess, showError } = useApiHandler();
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingActivity, setEditingActivity] = useState(null);
    const [viewMode, setViewMode] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [pagination, setPagination] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, activity: null });

    // Typing in the search box fetches once the user pauses, not per keystroke
    const debouncedSearchText = useDebouncedValue(searchText);

    useEffect(() => {
        fetchActivities(debouncedSearchText);
    }, [page, rowsPerPage, debouncedSearchText, statusFilter]);

    const fetchActivities = async (search = searchText, status = statusFilter) => {
        setLoading(true);
        try {
            const params = {
                page: page + 1, // Backend expects 1-based page numbers
                per_page: rowsPerPage
            };
            if (search) params.search = search;
            if (status !== '') params.status = status;

            const response = await activityService.getAllActivities(params);
            if (response.success) {
                setActivities(response.data || []);
                setTotalCount(response.pagination?.total || 0);
                setPagination(response.pagination);
                console.log('Pagination data:', response.pagination); // Debug log
                if (onDataChange) {
                    onDataChange();
                }
            } else {
                showError(response.message || 'Failed to fetch activities');
            }
        } catch (error) {
            console.error('Error fetching activities:', error);
            if (error.response?.data?.message) {
                showError(error.response.data.message);
            } else {
                showError('Failed to fetch activities. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (value) => {
        setSearchText(value);
        setPage(0); // Reset to first page when searching
    };

    const handleStatusFilter = (status) => {
        setStatusFilter(status);
        setPage(0); // Reset to first page when filtering
    };

    const handleAdd = () => {
        setEditingActivity(null);
        setViewMode(false);
        setIsModalVisible(true);
    };

    const handleEdit = (activity) => {
        setEditingActivity(activity);
        setViewMode(false);
        setIsModalVisible(true);
    };

    const handleView = (activity) => {
        setEditingActivity(activity);
        setViewMode(true);
        setIsModalVisible(true);
    };

    const handleDeleteClick = (activity) => {
        setDeleteDialog({ open: true, activity });
    };

    const handleDeleteConfirm = async () => {
        if (deleteDialog.activity) {
            try {
                const response = await activityService.deleteActivity(deleteDialog.activity.id);
                if (response.success) {
                    showSuccess('Activity deleted successfully');
                    fetchActivities();
                } else {
                    showError(response.message || 'Failed to delete activity');
                }
            } catch (error) {
                console.error('Error deleting activity:', error);
                showError(error.message || 'Failed to delete activity. Please try again.');
            }
        }
        setDeleteDialog({ open: false, activity: null });
    };

    const handleToggleStatus = async (activity) => {
        try {
            const response = await activityService.toggleActivityStatus(activity.id);
            if (response.success) {
                showSuccess('Activity status updated successfully');
                fetchActivities();
            } else {
                showError(response.message || 'Failed to update activity status');
            }
        } catch (error) {
            console.error('Error updating activity status:', error);
            showError(error.message || 'Failed to update activity status');
        }
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        setEditingActivity(null);
        setViewMode(false);
    };

    const handleFormSuccess = () => {
        setIsModalVisible(false);
        setEditingActivity(null);
        setViewMode(false);
        fetchActivities();
        const action = editingActivity ? 'updated' : 'created';
        showSuccess(`Activity ${action} successfully`);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Server-side pagination - no need for client-side filtering
    const paginatedActivities = activities;

    const getStatusChip = (status) => {
        return status === 1 ? (
            <Chip label="Active" color="success" size="small" />
        ) : (
            <Chip label="Inactive" color="default" size="small" />
        );
    };

    const getUsageChip = (count) => {
        if (count === 0) {
            return <Chip label="Unused" color="warning" size="small" />;
        } else if (count < 10) {
            return <Chip label={`${count} uses`} color="info" size="small" />;
        } else {
            return <Chip label={`${count} uses`} color="success" size="small" />;
        }
    };

    const isSystemProtected = (activity) => {
        return activity.id === 1 || activity.id === 8; // Activity ID 1 is system protected
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                    <Typography variant="h6" component="h2">
                        Activity List
                    </Typography>
                    {pagination && (
                        <Typography variant="caption" color="text.secondary">
                            Showing {pagination.from || 0} to {pagination.to || 0} of {pagination.total || 0} activities
                        </Typography>
                    )}
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAdd}
                >
                    Add Activity
                </Button>
            </Box>

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <TextField
                    placeholder="Search activities..."
                    value={searchText}
                    onChange={(e) => handleSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ minWidth: 300 }}
                />
                
                <FormControl sx={{ minWidth: 180 }}>
                    <InputLabel id="status-filter-label">Status Filter</InputLabel>
                    <Select
                        labelId="status-filter-label"
                        value={statusFilter}
                        label="Status Filter"
                        onChange={(e) => handleStatusFilter(e.target.value)}
                    >
                        <MenuItem value="">All Status</MenuItem>
                        <MenuItem value="1">Active</MenuItem>
                        <MenuItem value="0">Inactive</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* Table */}
            <TableContainer component={Paper} sx={{ position: 'relative' }}>
                {loading && (
                    <Box sx={{ 
                        position: 'absolute', 
                        top: 0, 
                        left: 0, 
                        right: 0, 
                        bottom: 0, 
                        bgcolor: 'rgba(255,255,255,0.8)', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        zIndex: 1
                    }}>
                        <CircularProgress />
                    </Box>
                )}
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>#</TableCell>
                            <TableCell>Activity Name</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Usage Count</TableCell>
                            <TableCell>Created Date</TableCell>
                            <TableCell>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedActivities.length === 0 && !loading ? (
                            <TableRow key="no-data">
                                <TableCell colSpan={6} align="center">
                                    <Typography variant="body2" color="text.secondary">
                                        No activities found
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            paginatedActivities.map((activity, index) => (
                                <TableRow key={activity.id}>
                                    <TableCell>{(pagination?.from || (page * rowsPerPage + 1)) + index}</TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <TimelineIcon sx={{ mr: 1, color: 'text.secondary' }} />
                                            {activity.name}
                                            {isSystemProtected(activity) && (
                                                <Chip 
                                                    label="System Protected" 
                                                    color="primary" 
                                                    size="small" 
                                                    variant="outlined"
                                                    sx={{ ml: 1, fontSize: '0.7rem', height: '20px' }}
                                                />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {getStatusChip(activity.status)}
                                            <Tooltip title={
                                                isSystemProtected(activity) 
                                                    ? "Cannot change status - System protected activity"
                                                    : activity.status === 1 
                                                        ? "Click to deactivate" 
                                                        : "Click to activate"
                                            }>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleToggleStatus(activity)}
                                                        color={activity.status === 1 ? "success" : "default"}
                                                        disabled={isSystemProtected(activity)}
                                                    >
                                                        {activity.status === 1 ? <ToggleOnIcon /> : <ToggleOffIcon />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        {getUsageChip(activity.usage_count)}
                                    </TableCell>
                                    <TableCell>
                                        {new Date(activity.created_at).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={1}>
                                            <Tooltip title="View Details">
                                                <IconButton 
                                                    size="small"
                                                    onClick={() => handleView(activity)}
                                                >
                                                    <ViewIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Edit Activity">
                                                <IconButton 
                                                    size="small"
                                                    onClick={() => handleEdit(activity)}
                                                >
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={
                                                isSystemProtected(activity)
                                                    ? "Cannot delete - System protected activity"
                                                    : activity.usage_count > 0 
                                                        ? "Cannot delete - Activity is in use" 
                                                        : "Delete Activity"
                                            }>
                                                <span>
                                                    <IconButton 
                                                        size="small"
                                                        onClick={() => handleDeleteClick(activity)}
                                                        disabled={activity.usage_count > 0 || isSystemProtected(activity)}
                                                        color="error"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />

            {/* Activity Form Modal */}
            <Modal
                open={isModalVisible}
                onClose={handleModalClose}
                aria-labelledby="activity-modal-title"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '90%', sm: '600px' },
                    maxHeight: '90vh',
                    overflow: 'auto',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 0
                }}>
                    <ActivityForm
                        activity={editingActivity}
                        onSuccess={handleFormSuccess}
                        onCancel={handleModalClose}
                        viewMode={viewMode}
                    />
                </Box>
            </Modal>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, activity: null })}
                aria-labelledby="delete-dialog-title"
            >
                <DialogTitle id="delete-dialog-title">
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the activity "{deleteDialog.activity?.name}"?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setDeleteDialog({ open: false, activity: null })}
                        color="inherit"
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ActivityList; 